import React, { useContext, useMemo, useRef, useState } from 'react';
import SlInput from '@shoelace-style/shoelace/dist/react/input/index.js';
import SlTextarea from '@shoelace-style/shoelace/dist/react/textarea/index.js';
import SlDialog from '@shoelace-style/shoelace/dist/react/dialog/index.js';
import SlButton from '@shoelace-style/shoelace/dist/react/button/index.js';
import SlTooltip from '@shoelace-style/shoelace/dist/react/tooltip/index.js';
import SlIcon from '@shoelace-style/shoelace/dist/react/icon/index.js';
import { ProductContext, productContext } from './Product';
import { AuthorPickerDialog } from './AuthorPickerDialog';
import { CategoryPickerDialog } from './CategoryPickerDialog';
import { GroupedPickerDialog, GroupedPickerItem } from './GroupedPickerDialog';
import { ErrorField } from '../../base/ErrorField/ErrorField';
import { useTranslation } from 'react-i18next';
import {
	FieldError,
	TextsToLocalize,
	LocaleCode,
	Author,
	MAX_TITLE_LENGTH,
	MAX_DESCRIPTION_LENGTH,
} from '@anticatrade/shared';
import { LocalizedField, Select, LocalizedFieldset } from '@anticatrade/shared';

const ProductInfo = () => {
	const [isAuthorDialogOpen, setIsAuthorDialogOpen] = useState<boolean>(false);
	const [isAuthorPickerOpen, setIsAuthorPickerOpen] = useState<boolean>(false);
	const [isCategoryPickerOpen, setIsCategoryPickerOpen] = useState<boolean>(false);
	const [isOriginPickerOpen, setIsOriginPickerOpen] = useState<boolean>(false);
	const [isMaterialsPickerOpen, setIsMaterialsPickerOpen] = useState<boolean>(false);

	const { t, i18n } = useTranslation();
	const {
		title,
		setTitle,
		description,
		setDescription,
		price,
		setPrice,
		conditionID,
		setConditionID,
		sku,
		setSku,
		categoryID,
		setCategoryID,
		centuryID,
		setCenturyID,
		styleID,
		setStyleID,
		authorID,
		setAuthorID,
		originID,
		setOriginID,
		proposedAuthor,
		setProposedAuthor,
		materialsIDs,
		setMaterialsIDs,
		categories,
		conditions,
		styles,
		centuries,
		authorName,
		setAuthorName,
		origins,
		materials,
		retrievedProduct,
		error,
		isCreation,
		showTranslations,
	} = useContext(productContext as React.Context<ProductContext>);

	const categoryName = useMemo(() => {
		if (categoryID == null || categories == null) {
			return undefined;
		}
		return categories.find((c) => c.id === categoryID)?.name;
	}, [categoryID, categories]);

	const onUpdateTitle = (e: any) => {
		const v = e.target.value;
		setTitle(v.trim() === '' ? undefined : v);
	};

	const onUpdateLocalizedTitle = (text: string, language: LocaleCode) => {
		const ti = title as TextsToLocalize;
		const tic = { ...ti };
		tic[language] = text.trim() === '' ? undefined : text;
		setTitle(tic);
	};

	const onUpdateDescription = (e: any) => {
		const v = e.target.value;
		setDescription(v.trim() === '' ? undefined : v);
	};

	const onUpdateLocalizedDescription = (text: string, language: LocaleCode) => {
		const l = description as TextsToLocalize;
		const lc = { ...l };
		lc[language] = text.trim() === '' ? undefined : text;
		setDescription(lc);
	};

	const onUpdatePrice = (e: any) => {
		const v = e.target.value;
		setPrice(v.trim() === '' ? undefined : Number(v));
	};

	const onChangeCondition = (e: any) => {
		setConditionID(Number(e.target.value));
	};

	const onUpdateSku = (e: any) => {
		const v = e.target.value;
		setSku(v.trim() === '' ? undefined : v);
	};

	const onSelectCategory = (id: number) => {
		setCategoryID(id);
		setIsCategoryPickerOpen(false);
	};

	const onChangeCentury = (e: any) => {
		setCenturyID(Number(e.target.value));
	};

	const onChangeStyle = (e: any) => {
		setStyleID(Number(e.target.value));
	};

	const onSelectAuthor = (author: Author) => {
		setAuthorID(author.id);
		setAuthorName(author.name);
		setProposedAuthor(undefined);
		setIsAuthorPickerOpen(false);
	};

	const onClearAuthor = (e: React.MouseEvent) => {
		e.stopPropagation();
		setAuthorID(undefined);
		setAuthorName(undefined);
	};

	const onChangeProposedAuthor = (author: string) => {
		setProposedAuthor(author);
		setAuthorID(undefined);
		setAuthorName(undefined);
		setIsAuthorDialogOpen(false);
	};

	const onClearProposedAuthor = () => {
		setProposedAuthor(undefined);
		setAuthorID(undefined);
		setAuthorName(undefined);
	};

	const onProposeAuthor = () => {
		setIsAuthorDialogOpen(true);
	};

	const onCancelProposeAuthor = () => {
		setIsAuthorDialogOpen(false);
	};

	const onConfirmOrigin = (ids: number[]) => {
		setOriginID(ids.length > 0 ? ids[0] : undefined);
		setIsOriginPickerOpen(false);
	};

	const onClearOrigin = (e: React.MouseEvent) => {
		e.stopPropagation();
		setOriginID(undefined);
	};

	const onConfirmMaterials = (ids: number[]) => {
		setMaterialsIDs(ids.length > 0 ? ids : undefined);
		setIsMaterialsPickerOpen(false);
	};

	const onRemoveMaterial = (e: React.MouseEvent, id: number) => {
		e.stopPropagation();
		if (materialsIDs == null) {
			return;
		}
		const ids = materialsIDs.filter((mid) => mid !== id);
		setMaterialsIDs(ids.length > 0 ? ids : undefined);
	};

	const originGroupLabels = useMemo(() => {
		if (origins == null) {
			return {};
		}
		const labels: Record<string, string> = {};
		for (const o of origins) {
			if (!labels[o.group]) {
				labels[o.group] = t(`group_${o.group}`);
			}
		}
		return labels;
	}, [origins, t]);

	const materialGroupLabels = useMemo(() => {
		if (materials == null) {
			return {};
		}
		const labels: Record<string, string> = {};
		for (const m of materials) {
			if (!labels[m.group]) {
				labels[m.group] = t(`group_${m.group}`);
			}
		}
		return labels;
	}, [materials, t]);

	const originName = useMemo(() => {
		if (originID == null || origins == null) {
			return undefined;
		}
		return origins.find((o) => o.id === originID)?.name;
	}, [originID, origins]);

	const selectedMaterialsList = useMemo(() => {
		if (materialsIDs == null || materials == null) {
			return [];
		}
		return materialsIDs
			.map((id) => {
				const m = materials.find((mat) => mat.id === id);
				return m ? { id: m.id, name: m.name } : null;
			})
			.filter((m) => m != null);
	}, [materialsIDs, materials]);

	const originItems: GroupedPickerItem[] = useMemo(() => {
		if (origins == null) {
			return [];
		}
		return origins.map((o) => ({ id: o.id, name: o.name, group: o.group }));
	}, [origins]);

	const materialItems: GroupedPickerItem[] = useMemo(() => {
		if (materials == null) {
			return [];
		}
		return materials.map((m) => ({ id: m.id, name: m.name, group: m.group }));
	}, [materials]);

	const localizedFieldsets = (
		<LocalizedFieldset initialLanguage={i18n.language as LocaleCode} t={t}>
			<ErrorField name='title' error={error}>
				<LocalizedField
					texts={title as TextsToLocalize}
					label={t('Title')}
					isTextarea={false}
					isRequired={true}
					onInputText={onUpdateLocalizedTitle}
					t={t}
					size='small'
				/>
			</ErrorField>
			<ErrorField name='description' error={error}>
				<LocalizedField
					texts={description as TextsToLocalize}
					label={t('Description')}
					isRequired={true}
					onInputText={onUpdateLocalizedDescription}
					t={t}
					rows={8}
					size='small'
				/>
			</ErrorField>
		</LocalizedFieldset>
	);

	if (showTranslations) {
		return (
			<div className='product__section product__section--info'>
				<div className='product__section-title'>{t('Review the translations')}</div>
				{localizedFieldsets}
			</div>
		);
	}

	return (
		<div className='product__section product__section--info'>
			<div className='product__section-form wrapped'>
				<div className='product__section-title'>{t('Texts and attributes')}</div>
				{isCreation ? (
					<div className='product__localized-fieldset'>
						<ErrorField
							name='title'
							error={error}
							footer={
								<span
									className={`product__char-count${
										(title == null ? 0 : [...(title as string)].length) >
										MAX_TITLE_LENGTH
											? ' product__char-count--over'
											: ''
									}`}
								>
									{title == null ? 0 : [...(title as string)].length} /{' '}
									{MAX_TITLE_LENGTH}
								</span>
							}
						>
							<SlInput
								className='product__title'
								label={t('Title')}
								onSlInput={onUpdateTitle}
								value={title === undefined ? '' : (title as string)}
								size='small'
								required
							/>
						</ErrorField>
						<ErrorField
							name='description'
							error={error}
							footer={
								<span
									className={`product__char-count${
										(description == null
											? 0
											: [...(description as string)].length) >
										MAX_DESCRIPTION_LENGTH
											? ' product__char-count--over'
											: ''
									}`}
								>
									{description == null ? 0 : [...(description as string)].length}{' '}
									/ {MAX_DESCRIPTION_LENGTH}
								</span>
							}
						>
							<SlTextarea
								className='product__description'
								label={t('Description')}
								onSlInput={onUpdateDescription}
								value={description === undefined ? '' : (description as string)}
								size='small'
								required
								rows={8}
								resize='none'
							/>
						</ErrorField>
					</div>
				) : (
					localizedFieldsets
				)}
				<ErrorField name='price' error={error}>
					<SlInput
						className='product__price'
						label={t('Price')}
						type='number'
						onSlInput={onUpdatePrice}
						value={price === undefined ? '' : String(price)}
						size='small'
						noSpinButtons
						required
					>
						<div className='product__price-currency' slot='suffix'>
							€
						</div>
					</SlInput>
				</ErrorField>
				<ErrorField name='condition' error={error}>
					<Select
						value={conditionID === undefined ? '' : String(conditionID)}
						label={t('Condition')}
						onChange={onChangeCondition}
						required
						className='product__condition'
						options={conditions?.map((c) => {
							return {
								key: c.id,
								name: c.name,
								value: String(c.id),
							};
						})}
						size='small'
					/>
				</ErrorField>
				<ErrorField name='category' error={error}>
					<div>
						<div className='product__picker-trigger-label'>
							{t('Category')}{' '}
							<span className='product__picker-required-symbol'>*</span>
						</div>
						<button
							className='product__picker-trigger'
							onClick={() => setIsCategoryPickerOpen(true)}
						>
							{categoryName ? (
								<span>{categoryName}</span>
							) : (
								<span className='product__picker-trigger-placeholder'>
									{t('Select a category')}
								</span>
							)}
						</button>
					</div>
				</ErrorField>
				<ErrorField name='century' error={error}>
					<Select
						required
						className='product__century'
						label={t('Epoch')}
						onChange={onChangeCentury}
						value={centuryID === undefined ? '' : String(centuryID)}
						options={centuries?.map((c) => {
							return {
								key: c.id,
								name: c.name,
								value: String(c.id),
							};
						})}
						size='small'
					/>
				</ErrorField>
				<ErrorField name='style' error={error}>
					<Select
						required
						className='product__style'
						label={t('Style')}
						onChange={onChangeStyle}
						value={styleID === undefined ? '' : String(styleID)}
						options={styles?.map((s) => {
							return {
								key: s.id,
								name: s.name,
								value: String(s.id),
							};
						})}
						size='small'
					/>
				</ErrorField>
				{proposedAuthor != null ? (
					<ErrorField name='proposed-author' error={error}>
						<div>
							<div className='product__proposed-author-wrapper'>
								<SlInput
									className='product__proposed-author-input'
									label={t('Author')}
									value={proposedAuthor}
									size='small'
									disabled
								/>
								<SlButton
									variant='neutral'
									onClick={onClearProposedAuthor}
									size='small'
								>
									{t('Cancel')}
								</SlButton>
							</div>
							<div className='product__proposed-author-text'>
								{isCreation
									? t(
											'This new author will automatically be created with your item',
										)
									: proposedAuthor !== retrievedProduct!.author?.name
										? t(
												'This new author will automatically be created when you save',
											)
										: t(
												'This author was recently created and is not yet part of the official list, we will add it as soon as we can',
											)}
							</div>
						</div>
					</ErrorField>
				) : (
					<ErrorField name='author' error={error}>
						<div>
							<div className='product__picker-trigger-label'>{t('Author')}</div>
							<button
								className='product__picker-trigger'
								onClick={() => setIsAuthorPickerOpen(true)}
							>
								{authorName ? (
									<span>{authorName}</span>
								) : (
									<span className='product__picker-trigger-placeholder'>
										{t('Select an author')}
									</span>
								)}
								{authorID != null && (
									<SlIcon
										className='product__picker-trigger-clear'
										name='x-circle'
										onClick={onClearAuthor}
									/>
								)}
							</button>
							<div className='product__author-propose'>
								{t("Can't find the author you're looking for?")}{' '}
								<button onClick={onProposeAuthor}>{t('Create it!')}</button>
							</div>
						</div>
					</ErrorField>
				)}
				<ErrorField name='origin' error={error}>
					<div>
						<div className='product__picker-trigger-label'>{t('Origin')}</div>
						<button
							className='product__picker-trigger'
							onClick={() => setIsOriginPickerOpen(true)}
						>
							{originName ? (
								<span>{originName}</span>
							) : (
								<span className='product__picker-trigger-placeholder'>
									{t('Select an origin')}
								</span>
							)}
							{originID != null && (
								<SlIcon
									className='product__picker-trigger-clear'
									name='x-circle'
									onClick={onClearOrigin}
								/>
							)}
						</button>
					</div>
				</ErrorField>
				<ErrorField name='materials' error={error}>
					<div>
						<div className='product__picker-trigger-label'>{t('Materials')}</div>
						<button
							className='product__picker-trigger'
							onClick={() => setIsMaterialsPickerOpen(true)}
						>
							{selectedMaterialsList.length > 0 ? (
								<div className='product__picker-chips'>
									{selectedMaterialsList.map((m) => (
										<span key={m.id} className='product__picker-chip'>
											{m.name}
											<SlIcon
												className='product__picker-chip-remove'
												name='x-circle'
												onClick={(e) => onRemoveMaterial(e, m.id)}
											/>
										</span>
									))}
								</div>
							) : (
								<span className='product__picker-trigger-placeholder'>
									{t('Select materials')}
								</span>
							)}
						</button>
					</div>
				</ErrorField>
				<ErrorField name='sku' error={error}>
					<SlInput
						className='product__sku'
						onSlInput={onUpdateSku}
						value={sku === undefined ? '' : sku}
						size='small'
					>
						<div className='product__sku-label' slot='label'>
							{t('Reference')}
							<SlTooltip content={t('Reference description')}>
								<SlIcon className='product__sku-icon' name='info-circle' />
							</SlTooltip>
						</div>
					</SlInput>
				</ErrorField>
			</div>
			<AuthorPickerDialog
				isOpen={isAuthorPickerOpen}
				selectedID={authorID}
				onHide={() => setIsAuthorPickerOpen(false)}
				onSelect={onSelectAuthor}
				onProposeAuthor={() => {
					setIsAuthorPickerOpen(false);
					setIsAuthorDialogOpen(true);
				}}
			/>
			<ProposeAuthorDialog
				isOpen={isAuthorDialogOpen}
				onHide={onCancelProposeAuthor}
				onPropose={onChangeProposedAuthor}
			/>
			<CategoryPickerDialog
				isOpen={isCategoryPickerOpen}
				categories={categories ?? []}
				selectedID={categoryID}
				onSelect={onSelectCategory}
				onHide={() => setIsCategoryPickerOpen(false)}
			/>
			<GroupedPickerDialog
				isOpen={isOriginPickerOpen}
				label={t('Origin')}
				items={originItems}
				groupLabels={originGroupLabels}
				mode='single'
				selectedIDs={originID != null ? [originID] : []}
				onConfirm={onConfirmOrigin}
				onHide={() => setIsOriginPickerOpen(false)}
				searchPlaceholder={t('Search origins...')}
			/>
			<GroupedPickerDialog
				isOpen={isMaterialsPickerOpen}
				label={t('Materials')}
				items={materialItems}
				groupLabels={materialGroupLabels}
				mode='multi'
				selectedIDs={materialsIDs ?? []}
				onConfirm={onConfirmMaterials}
				onHide={() => setIsMaterialsPickerOpen(false)}
				searchPlaceholder={t('Search materials...')}
				maxSelected={3}
			/>
		</div>
	);
};

interface ProposeAuthorDialogProps {
	isOpen: boolean;
	onHide: () => void;
	onPropose: (author: string) => void;
}

const ProposeAuthorDialog = ({ isOpen, onHide, onPropose }: ProposeAuthorDialogProps) => {
	const [name, setName] = useState<string>('');
	const [error, setError] = useState<FieldError>();

	const nameInputRef = useRef<any>(null);

	const { t } = useTranslation();

	const onInputName = (e: any) => {
		setName(e.target.value);
	};

	const onProposeAuthor = () => {
		setError(undefined);
		try {
			if (name === '') {
				throw new Error(t("Author's name is required"));
			}
		} catch (err) {
			const e = err as Error;
			const fError = new FieldError(e.message, 'author-name');
			setTimeout(() => {
				setError(fError);
			}, 100);
			return;
		}
		onPropose(name);
	};

	const onAfterHide = () => {
		setTimeout(() => {
			setName('');
		}, 500);
		onHide();
	};

	return (
		<SlDialog
			className='product__propose-author-dialog'
			label={t('Create an author')}
			open={isOpen}
			onSlAfterHide={onAfterHide}
			onSlAfterShow={() => nameInputRef.current?.focus()}
		>
			<ErrorField name='author-name' error={error}>
				<SlInput
					ref={nameInputRef}
					label={t("Author's full name")}
					placeholder={t('e.g. Cafiero Filippelli, Giovanni Lomi')}
					onSlInput={onInputName}
					value={name}
				/>
			</ErrorField>
			<SlButton
				className='product__propose-author-button'
				variant='primary'
				onClick={onProposeAuthor}
			>
				{t('Create')}
			</SlButton>
		</SlDialog>
	);
};

export { ProductInfo };
